import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { invokeTracedFunction } from "@/lib/supabase-traced";
import { logger } from "@/lib/logger";
import { useRef } from "react";

interface VoiceCoachPreferences {
  voiceEnabled: boolean;
  voiceGender: 'female' | 'male';
  voiceFrequency: 'minimal' | 'normal' | 'frequent';
}

interface RunContext {
  currentPace?: number;
  targetPace?: number;
  currentBPM?: number;
  targetBPM?: number;
  distance?: number;
  duration?: number;
  zone?: string;
  lastKmPace?: number;
  cadence?: number;
  elevation?: number;
  userLevel?: number;
}

interface CoachMessage {
  type: 'pace' | 'bpm' | 'zone' | 'milestone' | 'technique' | 'motivation';
  priority: 'low' | 'normal' | 'high';
  message: string;
}

export const useVoiceCoach = () => {
  const { toast } = useToast();
  const lastMessageRef = useRef<{ type: string; timestamp: number } | null>(null);
  const messageQueueRef = useRef<CoachMessage[]>([]);

  const { data: preferences, isLoading } = useQuery({
    queryKey: ['voice-coach-preferences'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return null;

      const { data } = await supabase
        .from('profiles')
        .select('voice_coaching_enabled, voice_gender, voice_frequency')
        .eq('id', user.id)
        .single();

      return {
        voiceEnabled: data?.voice_coaching_enabled ?? true,
        voiceGender: (data?.voice_gender as 'female' | 'male') ?? 'female',
        voiceFrequency: (data?.voice_frequency as 'minimal' | 'normal' | 'frequent') ?? 'normal',
      } as VoiceCoachPreferences;
    },
  });

  const shouldSpeak = (type: string): boolean => {
    if (!preferences?.voiceEnabled) return false;
    
    const now = Date.now();
    const lastMessage = lastMessageRef.current;
    
    // Frequency thresholds (in milliseconds)
    const thresholds = {
      minimal: 300000, // 5 minutes
      normal: 120000,  // 2 minutes
      frequent: 60000  // 1 minute
    };
    
    const threshold = thresholds[preferences.voiceFrequency];
    
    // Always allow high priority messages
    if (type === 'zone' || type === 'milestone') return true;
    
    // Check cooldown for other messages
    if (lastMessage && now - lastMessage.timestamp < threshold) {
      return false;
    }
    
    return true;
  };

  const generateContextualMessage = (context: RunContext): string | null => {
    const { currentPace, targetPace, currentBPM, targetBPM, distance, zone, cadence, userLevel } = context;
    
    // Pace feedback
    if (currentPace && targetPace) {
      const paceVariance = ((currentPace - targetPace) / targetPace) * 100;
      if (Math.abs(paceVariance) > 10) {
        if (paceVariance > 0) {
          return userLevel && userLevel < 5 
            ? "Você está um pouco devagar. Tente acelerar gradualmente."
            : "Ritmo abaixo do alvo. Aumente a cadência progressivamente.";
        } else {
          return userLevel && userLevel < 5
            ? "Você está muito rápido! Reduza o ritmo para não se cansar."
            : "Ritmo acima do alvo. Mantenha controle para preservar energia.";
        }
      }
    }
    
    // Heart rate zone alerts
    if (zone === 'anaerobic' || zone === 'maximum') {
      return "Frequência cardíaca muito alta! Reduza a intensidade para zona aeróbica.";
    }
    
    // BPM feedback
    if (currentBPM && targetBPM) {
      const bpmVariance = currentBPM - targetBPM;
      if (bpmVariance > 15) {
        return "Seu coração está acelerado. Respire fundo e mantenha o controle.";
      }
    }
    
    // Cadence technique
    if (cadence && (cadence < 160 || cadence > 190)) {
      if (cadence < 160) {
        return "Aumente a cadência. Passos mais rápidos e curtos são mais eficientes.";
      } else {
        return "Cadência muito alta. Relaxe e encontre seu ritmo natural.";
      }
    }
    
    // Milestone encouragement
    if (distance) {
      const kmMilestone = Math.floor(distance);
      if (distance - kmMilestone < 0.05 && distance > 1) {
        const messages = [
          `${kmMilestone} quilômetros! Continue firme!`,
          `Mais ${kmMilestone}km na conta! Você está arrasando!`,
          `${kmMilestone}km concluídos! Força!`
        ];
        return messages[Math.floor(Math.random() * messages.length)];
      }
    }
    
    return null;
  };

  const speakCoach = async (message: string) => {
    if (!preferences?.voiceEnabled) {
      logger.debug('Voice Coach disabled by user', { voiceEnabled: false });
      return;
    }

    try {
      const { data, error } = await invokeTracedFunction('voice-coach-speak', { 
        text: message, 
        voiceGender: preferences.voiceGender 
      });

      if (error) throw error;

      const audioBlob = new Blob([data], { type: 'audio/mpeg' });
      const audioUrl = URL.createObjectURL(audioBlob);
      const audio = new Audio(audioUrl);
      
      await audio.play();
      
      audio.onended = () => URL.revokeObjectURL(audioUrl);
      
      lastMessageRef.current = { type: 'general', timestamp: Date.now() };
    } catch (error) {
      logger.error('Voice Coach error', error as Error, { 
        message,
        voiceGender: preferences?.voiceGender 
      });
      toast({
        title: "Voice Coach indisponível",
        description: "Não foi possível reproduzir o áudio.",
        variant: "destructive"
      });
    }
  };

  const speakContextual = async (context: RunContext, forceType?: string) => {
    const message = forceType ? context : generateContextualMessage(context);
    
    if (typeof message === 'string' && message && shouldSpeak(forceType || 'general')) {
      await speakCoach(message);
    }
  };

  return {
    preferences,
    isLoading,
    speakCoach,
    speakContextual,
    shouldSpeak,
  };
};
